import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { Proposal, ProposalStatus, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import KalaKrutGovernorABI from '@/abis/KalaKrutGovernor.json';
import { ethers, EventLog } from 'ethers';

const StatusPill: React.FC<{ status: ProposalStatus }> = ({ status }) => {
    const statusStyle = {
        [ProposalStatus.PENDING]: 'bg-yellow-500/20 text-yellow-300',
        [ProposalStatus.ACTIVE]: 'bg-blue-500/20 text-blue-300',
        [ProposalStatus.PASSED]: 'bg-green-500/20 text-green-300',
        [ProposalStatus.EXECUTED]: 'bg-green-700/20 text-green-500',
        [ProposalStatus.FAILED]: 'bg-red-500/20 text-red-300',
    }[status] || 'bg-gray-500/20 text-gray-300';

    return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyle}`}>{status}</span>;
};

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>{children}</button>
);

const TableActions: React.FC<{ 
  proposal: Proposal; 
  role: UserRole; 
  onAction: (action: string, proposalId: string) => void;
}> = ({ proposal, role, onAction }) => {
  const handleAction = (action: string) => { onAction(action, proposal.id); };

  switch (role) {
    case UserRole.DAO_GOVERNOR:
      return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('approve')} className="bg-green-500/20 text-green-300 hover:bg-green-500/40">Approve</ActionButton>
          <ActionButton onClick={() => handleAction('reject')} className="bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/40">Reject</ActionButton>
        </div>
      );
    case UserRole.ADMIN:
    case UserRole.SYSTEM_ADMIN_LIVE:
      return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('view')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">View</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
    case UserRole.DAO_MEMBER:
      return <ActionButton onClick={() => handleAction('view')} className="bg-gray-500/20 text-gray-300 hover:bg-gray-500/40">View</ActionButton>;
    default:
      return <></>;
  }
};

const DAOProposalRealDataTable = ({ currentUserRole, currentUserId }: { currentUserRole: UserRole, currentUserId: string }) => {
  const [allProposals, setAllProposals] = useState<Proposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFallback, setIsFallback] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const navigate = useNavigate();
  const { provider, isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsFallback(false);
    addLog("Starting DAO proposal data fetch.", LogLevel.INFO, LogCategory.DATA);

    // On-Chain First Approach
    if (provider && isConnected) {
      try {
        const governorContract = new ethers.Contract(contractAddresses.KalaKrutGovernor, KalaKrutGovernorABI.abi, provider);
        const proposalEvents = await governorContract.queryFilter(governorContract.filters.ProposalCreated());

        const proposals = await Promise.all((proposalEvents as EventLog[]).map(async (event) => {
          const proposalId = event.args.proposalId.toString();
          const state = await governorContract.state(proposalId);
          // Fetch votes for and against from the contract
          const [againstVotes, forVotes] = await governorContract.proposalVotes(proposalId);

          return {
            id: proposalId,
            title: event.args.description,
            description: event.args.description, // Added this line
            proposer: { id: event.args.proposer, name: `${event.args.proposer.substring(0, 6)}...${event.args.proposer.substring(event.args.proposer.length - 4)}`, role: UserRole.DAO_MEMBER },
            status: Object.values(ProposalStatus)[state] || ProposalStatus.PENDING,
            createdAt: new Date(Number(event.args.voteStart) * 1000),
            votes: [], // Placeholder, as we have counts directly
            votesFor: Number(ethers.formatUnits(forVotes, 0)),
            votesAgainst: Number(ethers.formatUnits(againstVotes, 0)),
          } as Proposal;
        }));
        setAllProposals(proposals.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        addLog(`Fetched ${proposals.length} on-chain proposals.`, LogLevel.SUCCESS, LogCategory.DATA);
        setIsLoading(false);
        return;
      } catch (e) {
        const onChainError = "Could not load on-chain proposal data. Falling back to API.";
        addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
        setError(onChainError);
        notify(onChainError, 'warn');
        setIsFallback(true); // Trigger fallback
      }
    }

    // Fallback to Off-Chain API
    try {
      let apiData;
      const response = await fetch(`${API_BASE_URL}/api/proposals/dao`, { credentials: 'include' });

      if (response.ok) {
        apiData = await response.json();
        addLog(`Fetched ${apiData.length} proposals from REAL API as fallback.`, LogLevel.INFO, LogCategory.API);
      } else {
        addLog(`Real API fallback failed, using mock data.`, LogLevel.WARN, LogCategory.API);
        const mockResponse = await fetch('/dao-proposals.json');
        if (!mockResponse.ok) throw new Error('Real API and mock data both failed.');
        apiData = await mockResponse.json();
        notify('Displaying mock data. Wallet not connected and API failed.', 'warn');
      }
      
      if (Array.isArray(apiData)) {
        const formattedProposals: Proposal[] = apiData.map(p => ({ ...p, votesFor: 0, votesAgainst: 0 }));
        setAllProposals(formattedProposals.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        setIsFallback(true);
      } else {
        throw new Error('Fetched off-chain data is not an array.');
      }

    } catch (e) {
      const fallbackError = `Critical: Failed to load any proposal data: ${(e as Error).message}`;
      addLog(fallbackError, LogLevel.CRITICAL, LogCategory.API);
      setError(fallbackError);
      notify(fallbackError, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (action: string, proposalId: string) => {
    if (action === 'view') {
      navigate(`/dao/proposals/${proposalId}`);
    } else if (action === 'delete') {
      if (!window.confirm('Are you sure you want to delete this proposal?')) return;
      
      addLog(`User ${currentUserId} initiated delete for proposal ${proposalId}`, LogLevel.INFO, LogCategory.DAO);
      try {
        const response = await fetch(`${API_BASE_URL}/api/proposals/${proposalId}`, { method: 'DELETE', credentials: 'include' });
        if (!response.ok) throw new Error('Failed to delete proposal from API. This action might not be supported if data is from blockchain.');
        setAllProposals(current => current.filter(p => p.id !== proposalId));
        notify(`Proposal ${proposalId} deleted.`, 'success');
        addLog(`DAO proposal ${proposalId} successfully deleted by user ${currentUserId}`, LogLevel.INFO, LogCategory.DAO);
      } catch (err) {
        notify((err as Error).message, 'error');
        addLog(`Failed to delete DAO proposal ${proposalId}: ${(err as Error).message}`, LogLevel.ERROR, LogCategory.DAO);
      }
    } else {
      notify(`Action '${action}' for proposal '${proposalId}' not implemented.`, 'info');
    }
  };

  const filteredProposals = useMemo(() => {
    return allProposals.filter(p => 
      p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.proposer?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allProposals, searchTerm]);

  const displayedProposals = useMemo(() => {
      return filteredProposals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredProposals, page]);

  const hasMore = page * PAGE_SIZE < filteredProposals.length;

  if (isLoading && allProposals.length === 0) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading DAO proposals...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">DAO Proposals</h2>
        <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
                type="text"
                placeholder="Search proposals..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
            />
        </div>
      </div>
      {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
        {isFallback ? (
            <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
            Note: Wallet disconnected. On-chain data is unavailable.
            </div>
        ) : (
            <div className="p-2 mb-4 bg-green-500/10 text-green-400 text-center text-[10px] uppercase tracking-widest border-y border-green-700">
            Displaying live on-chain data.
            </div>
        )}

      <Table>
        <TableCaption>A list of all DAO proposals. {allProposals.length === 0 && !isLoading && "No proposals found."}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Proposer</TableHead>
            <TableHead>Votes For</TableHead>
            <TableHead>Votes Against</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedProposals.map((proposal) => (
            <TableRow key={proposal.id}>
              <TableCell>{proposal.title}</TableCell>
              <TableCell>{proposal.proposer?.name || 'N/A'}</TableCell>
              <TableCell>{proposal.votesFor ?? 0}</TableCell>
              <TableCell>{proposal.votesAgainst ?? 0}</TableCell>
              <TableCell><StatusPill status={proposal.status} /></TableCell>
              <TableCell>{new Date(proposal.createdAt).toLocaleDateString()}</TableCell>
              <TableCell className="text-right">
                <TableActions proposal={proposal} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

        {displayedProposals.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No proposals found for your search term.</p>
            </div>
        )}

      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredProposals.length / PAGE_SIZE)}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DAOProposalRealDataTable;
